import { useInView } from '../../hooks/useInView';
import { Leaf, FlaskConical, Atom, Shield, Zap } from 'lucide-react';

const ingredients = [
  {
    icon: Leaf,
    name: 'Pygeum Africanum',
    dose: '500mg',
    subtitle: 'Prostate & Seminal Fluid Support',
    benefit: 'Supports prostate health and seminal fluid production. Rich in naturally occurring phytosterols that support male reproductive function.',
    tag: 'Botanical Extract',
  },
  {
    icon: Zap,
    name: 'Catuaba Bark',
    dose: '465mg',
    subtitle: 'Libido & Sexual Vitality',
    benefit: 'Traditionally used to support libido and sexual performance. Helps promote vitality and male reproductive health.',
    tag: 'Amazonian Herb',
  },
  {
    icon: FlaskConical,
    name: 'Korean Ginseng',
    dose: '400mg',
    subtitle: 'Hormonal & Reproductive Support',
    benefit: 'Clinically studied adaptogenic root that supports testosterone levels, libido, and male reproductive health. Used in Korean traditional medicine for centuries.',
    tag: 'Adaptogenic Root',
  },
  {
    icon: Atom,
    name: 'Zinc',
    dose: '10mg',
    subtitle: 'Male Reproductive Support',
    benefit: 'Supports testosterone levels, sperm production, and male reproductive health. Essential mineral for reproductive function.',
    tag: 'Essential Mineral',
  },
  {
    icon: Shield,
    name: 'Selenium',
    dose: '55mcg',
    subtitle: 'Antioxidant & Sperm Health',
    benefit: 'Supports antioxidant protection, sperm motility, and morphology. Works synergistically with zinc to support male reproductive health.',
    tag: 'Trace Mineral',
  },
];

export default function IngredientsSection() {
  const [ref, inView] = useInView(0.05);

  return (
    <section
      id="ingredients"
      data-testid="ingredients-section"
      style={{ backgroundColor: '#F7F7F5', padding: '96px 0', position: 'relative', overflow: 'hidden' }}
    >
      {/* Subtle botanical background */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/89897a2f-da0f-442c-92e9-3b70bf1115c6/images/dd147cd71d5f6b119f7139eceff049ab6ff297d5a73671d926069a4ead191092.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.04,
        pointerEvents: 'none',
      }} />

      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">

        {/* Header */}
        <div className={`text-center mb-16 fade-up ${inView ? 'visible' : ''}`}>
          <span className="overline block mb-4">What&apos;s Inside</span>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: '#0D1A2E',
            letterSpacing: '-0.025em',
            marginBottom: '16px',
          }}>
            The RELOAD Performance Formula
          </h2>
          <p style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '1rem',
            color: '#64748B',
            maxWidth: '560px',
            margin: '0 auto',
            lineHeight: 1.8,
          }}>
            Scientifically selected ingredients to support semen volume, libido, and male reproductive health.
          </p>
        </div>

        {/* Ingredient Cards */}
        <div className="flex flex-col gap-4">
          {ingredients.map((ing, i) => (
            <div
              key={ing.name}
              data-testid={`ingredient-card-${i}`}
              className={`card-light rounded-2xl fade-up delay-${Math.min(i + 1, 4)} ${inView ? 'visible' : ''}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(13,26,46,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
            >
              {/* Left: blue accent bar */}
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{ width: '4px', backgroundColor: '#1B3D7E', flexShrink: 0, borderRadius: '0' }} />

                <div style={{ padding: '28px 28px 28px 24px', display: 'flex', gap: '20px', alignItems: 'flex-start', flex: 1 }}>
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(27,61,126,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px',
                  }}>
                    <ing.icon size={20} style={{ color: '#1B3D7E' }} strokeWidth={1.5} />
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1 }}>
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: '#0D1A2E',
                        lineHeight: 1.2,
                      }}>
                        {ing.name}
                      </h3>
                      {/* Tag pill */}
                      <span style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#64748B',
                        backgroundColor: '#EEF1F6',
                        border: '1px solid #E2E8F0',
                        padding: '3px 10px',
                        borderRadius: '100px',
                      }}>
                        {ing.tag}
                      </span>
                    </div>

                    {/* Benefit subtitle */}
                    <p style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: '#1B3D7E',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}>
                      {ing.subtitle}
                    </p>

                    {/* Description */}
                    <p style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '0.88rem',
                      color: '#64748B',
                      lineHeight: 1.8,
                      maxWidth: '700px',
                    }}>
                      {ing.benefit}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right: Dosage badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 32px',
                borderLeft: '1px solid #E2E8F0',
                minWidth: '120px',
                backgroundColor: '#FAFBFD',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: '#1B3D7E',
                    lineHeight: 1,
                    marginBottom: '4px',
                  }}>
                    {ing.dose}
                  </p>
                  <p style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: '#94A3B8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                  }}>
                    Per Serving
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className={`text-center mt-10 fade-up delay-4 ${inView ? 'visible' : ''}`}>
          <p style={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '0.78rem',
            color: '#94A3B8',
            lineHeight: 1.7,
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            Food supplement. Not intended to diagnose, treat, cure, or prevent any disease.
            Always read the label. Do not exceed the recommended daily dose.
          </p>
        </div>

      </div>
    </section>
  );
}
